import { Box, CircularProgress, Dialog, IconButton, LinearProgress, Typography } from "@mui/material"
import {
    CloseOutlined, HomeWorkOutlined, LocationOnOutlined,
    PeopleOutlined, TrendingUpOutlined, WarningAmberOutlined
} from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import { Apis, getImageUrl, numberWithCommas } from "../../helpers"
import NoData from "../Common/NoData"
import { PropertyProps } from "../../types/properties"

const PRIMARY = '#8c7c0c'
const PRIMARY_LIGHT = '#f5f2e0'
const WARM_GREEN = '#6a8c0c'
const WARM_AMBER = '#d4890a'
const WARM_RED = '#c05c13'

const PropertyCard: FC<{ item: PropertyProps }> = ({ item }) => {
    const [imgFailed, setImgFailed] = useState(false)
    const { totalUnits, occupiedUnits, placeName, currentMonthRevenue, arrears, logos, name, location } = item
    const occupancyPct = totalUnits > 0 ? Math.round(occupiedUnits * 100 / totalUnits) : 0
    const occupancyColor = occupancyPct >= 80 ? WARM_GREEN : occupancyPct >= 50 ? WARM_AMBER : WARM_RED
    const logoUrl = getImageUrl(logos, 'small')
    const showFallback = !logoUrl || imgFailed
    const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

    const stats = [
        { icon: <HomeWorkOutlined sx={{ fontSize: 14 }} />, label: 'Units', value: String(totalUnits), color: PRIMARY },
        { icon: <PeopleOutlined sx={{ fontSize: 14 }} />, label: 'Occupied', value: `${occupancyPct}%`, color: occupancyColor },
        { icon: <TrendingUpOutlined sx={{ fontSize: 14 }} />, label: 'Collected', value: `Ksh ${numberWithCommas(currentMonthRevenue)}`, color: WARM_GREEN },
        { icon: <WarningAmberOutlined sx={{ fontSize: 14 }} />, label: 'Arrears', value: `Ksh ${numberWithCommas(arrears)}`, color: arrears > 0 ? WARM_RED : WARM_GREEN },
    ]

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', borderRadius: 3, p: 2, mb: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)', flexWrap: 'wrap'
        }}>
            <Box sx={{
                width: 52, height: 52, borderRadius: 2, flexShrink: 0, overflow: 'hidden',
                border: '2px solid rgba(140,124,12,.15)', bgcolor: PRIMARY_LIGHT, display: 'flex',
                alignItems: 'center', justifyContent: 'center'
            }}>
                {!showFallback
                    ? <Box
                        component="img"
                        src={logoUrl}
                        alt={name}
                        onError={() => setImgFailed(true)}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    : <Typography sx={{ fontWeight: 800, fontSize: 16, color: PRIMARY, userSelect: 'none' }}>{initials}</Typography>
                }
            </Box>
            <Box sx={{ flex: '0 0 200px', minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }} noWrap>{name}</Typography>
                {(placeName || location?.locName) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                        <LocationOnOutlined sx={{ fontSize: 11, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>
                            {[placeName, location?.locName].filter(Boolean).join(', ')}
                        </Typography>
                    </Box>
                )}
                <LinearProgress
                    variant="determinate"
                    value={occupancyPct}
                    sx={{
                        mt: 1, height: 3, borderRadius: 2, bgcolor: '#f1f5f9', width: 140,
                        '& .MuiLinearProgress-bar': { bgcolor: occupancyColor, borderRadius: 2 }
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flex: 1, flexWrap: 'wrap' }}>
                {stats.map(({ icon, label, value, color }) => (
                    <Box key={label} sx={{ minWidth: 72 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                            <Box sx={{ color }}>{icon}</Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                                {label}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color }}>{value}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

interface Props {
    landlordUserId: string
    landlordName: string
    close: () => void
}

interface StateProps {
    loading: boolean
    properties: PropertyProps[]
}

const LandlordPropertiesModal: FC<Props> = ({ landlordUserId, landlordName, close }) => {
    const [state, setState] = useState<StateProps>({ loading: true, properties: [] })
    const { loading, properties } = state

    useEffect(() => {
        Apis({ module: 'Properties', action: 'getPropertiesInfo', landlordUserId, page: 0, pageSize: 100 })
            .then((resp: any) => setState({ loading: false, properties: resp.properties || [] }))
            .catch(() => setState({ loading: false, properties: [] }))
        // eslint-disable-next-line
    }, [landlordUserId])

    return (
        <Dialog open onClose={close} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Properties</Typography>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.2 }}>{landlordName}</Typography>
                    </Box>
                    <IconButton size="small" onClick={close}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                {loading
                    ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={26} /></Box>
                    : !properties.length
                        ? <NoData message="No properties found." />
                        : <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                            {properties.map(item => <PropertyCard item={item} key={item._id} />)}
                        </Box>
                }
            </Box>
        </Dialog>
    )
}

export default LandlordPropertiesModal
